import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqItems } from '@/mocks/testimonials';

const advantages = [
  {
    icon: 'ri-bookmark-line',
    title: '海量韩漫\n资源库',
    description: '收录超过数千部优质韩国漫画，涵盖奇幻、恋爱、悬疑、校园等全品类，每日持续更新。',
  },
  {
    icon: 'ri-eye-line',
    title: '无删减\n原版内容',
    description: '坚持提供韩国原版无删减漫画内容，完整呈现创作者的艺术表达与故事情节。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '下拉式\n流畅阅读',
    description: '创新的下拉式阅读模式，无需翻页等待，一滑到底，带来极致流畅的阅读享受。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '安全可靠\n值得信赖',
    description: '无广告干扰，无恶意弹窗，保护用户隐私，打造纯净安全的阅读环境。',
  },
];

const milestones = [
  { year: '2024', title: '平台创立', desc: '韩漫大全正式上线，初始收录500部热门韩漫作品。' },
  { year: '2025', title: '快速成长', desc: '漫画库扩展至2000+部，推出下拉式阅读功能，用户突破10万。' },
  { year: '2026', title: '行业领先', desc: '成为中文韩漫领域的领先平台，日均活跃用户超过50万。' },
];

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      formData.delete('website_alt');
      const response = await fetch('http://jorjigsrp.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const breadcrumbListLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://jorjigsrp.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'http://jorjigsrp.com/about',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbListLd),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 韩漫大全',
            description: '韩漫大全是一个专注于提供韩国漫画无删减内容的免费在线阅读平台。海量优质韩漫资源，下拉式流畅阅读。',
            url: 'http://jorjigsrp.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫大全',
              url: 'http://jorjigsrp.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="pt-16">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-16">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-[40%]">
                  <span className="inline-block px-3 py-1 text-xs font-medium border border-foreground-200/70 rounded-full text-foreground-600 mb-6 whitespace-nowrap">
                    品牌故事
                  </span>

                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                    <span className="font-bold">关于韩漫大全</span>
                  </h1>
                  <p className="text-lg font-heading text-foreground-500 mb-8">
                    专注韩国漫画 · 无删减阅读平台
                  </p>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-md">
                    韩漫大全创立于2024年，是一个专注于提供韩国漫画无删减内容的免费在线阅读平台。我们深知广大韩漫爱好者在寻找高质量、完整版韩国漫画时的痛点——删减内容、满屏广告、翻页卡顿。因此，我们致力于打造一个资源丰富、体验流畅、完全免费的韩国漫画阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-md">
                    从最初的500部漫画到如今覆盖数千部优质作品，韩漫大全始终坚持无删减原则，并推出了创新的下拉式阅读模式，让每一位读者都能享受到最纯粹的韩漫阅读乐趣。
                  </p>

                  <div className="flex items-center gap-8 mb-8">
                    <div>
                      <span className="block text-2xl font-bold text-foreground-900">3000+</span>
                      <span className="text-xs text-foreground-500">收录作品</span>
                    </div>
                    <div>
                      <span className="block text-2xl font-bold text-foreground-900">3年</span>
                      <span className="text-xs text-foreground-500">运营时间</span>
                    </div>
                  </div>

                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap group border-b border-primary-300 pb-0.5"
                  >
                    联系我们
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform text-xs"></i>
                  </a>
                </div>

                <div className="lg:w-[60%] flex gap-4">
                  <div className="w-[45%] aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20of%20creative%20team%20working%20on%20digital%20comics%2C%20warm%20cozy%20studio%20atmosphere%2C%20clean%20line%20art%20style%2C%20soft%20natural%20lighting%2C%20modern%20workspace%2C%20high%20quality%20digital%20art&width=800&height=1100&seq=about-brand-story-01&orientation=portrait"
                      alt="韩漫大全团队"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-[55%]">
                    <div className="aspect-[4/5] rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20manhwa%20reading%20concept%20art%2C%20digital%20comic%20panels%20cascading%20in%20warm%20amber%20tones%2C%20artistic%20composition%20with%20flowing%20scroll%20effect%2C%20clean%20background%2C%20high%20quality%20illustration&width=800&height=1200&seq=about-brand-story-02&orientation=portrait"
                        alt="韩漫阅读概念"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 text-xs font-medium border border-foreground-200/70 rounded-full text-foreground-600 mb-4 whitespace-nowrap">
                  发展历程
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  我们的成长之路
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  从创立之初到行业领先，每一步都离不开读者的支持
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {milestones.map((m, index) => (
                  <div key={index} className="text-center bg-background-50 rounded-lg p-6">
                    <span className="inline-block text-3xl font-bold text-accent-500 mb-3">{m.year}</span>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{m.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 text-xs font-medium border border-foreground-200/70 rounded-full text-foreground-600 mb-4 whitespace-nowrap">
                  平台优势
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  为什么选择我们
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  韩漫大全的四大核心优势，为你带来最佳阅读体验
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {advantages.map((adv, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-6 md:p-7 hover:bg-primary-50 transition-colors duration-300 group cursor-pointer">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-5 group-hover:bg-primary-100 transition-colors duration-300">
                      <i className={`${adv.icon} text-lg text-secondary-600 group-hover:text-primary-500 transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 whitespace-pre-line leading-snug">{adv.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{adv.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="flex flex-col lg:flex-row" id="contact">
            <div className="lg:w-1/2 relative h-[280px] md:h-[400px] bg-secondary-100 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20office%20environment%20with%20creative%20team%20discussion%2C%20Korean%20manhwa%20style%20illustrations%20on%20walls%2C%20soft%20natural%20light%20from%20large%20windows%2C%20cozy%20atmosphere%2C%20clean%20composition%2C%20beige%20and%20cream%20tones&width=1200&height=900&seq=about-contact-image&orientation=landscape"
                alt="联系我们"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary-100/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="text-xs font-light text-foreground-700 whitespace-nowrap">合作共赢 · 共创未来</span>
              </div>
              <div className="absolute bottom-6 left-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  期待与您<br />携手同行
                </h2>
              </div>
            </div>

            <div className="lg:w-1/2 bg-background-50 flex items-center justify-center py-12 md:py-16 px-6 md:px-12">
              <div className="max-w-md w-full">
                <div className="text-left mb-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您有任何问题或合作意向，欢迎通过以下表单与我们联系。
                  </p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200"
                        placeholder="请选择或输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>

                    <div className="honeypot-field">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-xs text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-xs text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-secondary-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于韩漫大全的常见疑问</p>
              </div>

              <div className="max-w-3xl mx-auto space-y-3">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-background-50 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                      <i className={`${faqOpen === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 text-sm flex-shrink-0 transition-transform duration-200`}></i>
                    </button>
                    {faqOpen === index && (
                      <div className="px-5 pb-4">
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}